import { ref, computed } from 'vue';
import { http } from '@/helpers';

/**
 * Composable pour gérer les paramètres de l'application avec mise en cache localStorage
 * @param {Object} options Options de configuration
 * @param {number} options.cacheTime Temps minimum en millisecondes entre deux rafraîchissements (défaut: 5 minutes)
 * @returns {Object} Fonctions et propriétés pour gérer les paramètres
 */
export function useParameters(options = {}) {
    // Configuration
    const cacheTime = options.cacheTime || 5 * 60 * 1000; // 5 minutes par défaut
    const localStorageKey = 'nebulone_parameters';
    const localStorageTimeKey = 'nebulone_parameters_timestamp';

    // État
    const parameters = ref([]);
    const parameterMap = ref(new Map());
    const loading = ref(false);
    const error = ref(null);
    const lastRefresh = ref(0);

    const { authStore } = useAuth();
    const { isConnected } = storeToRefs(authStore);

    /**
     * Charge les paramètres depuis le localStorage
     * @returns {boolean} true si les paramètres ont été chargés depuis le localStorage
     */
    function loadFromLocalStorage() {
        try {
            // Vérifier si les données en cache existent
            const cachedParams = localStorage.getItem(localStorageKey);
            const cachedTimestamp = localStorage.getItem(localStorageTimeKey);

            if (!cachedParams || !cachedTimestamp) return false;

            // Charger les données
            const parsedParams = JSON.parse(cachedParams);
            const timestamp = parseInt(cachedTimestamp, 10);

            // Vérifier si les données sont valides
            if (!Array.isArray(parsedParams) || isNaN(timestamp)) return false;

            parameters.value = parsedParams;
            lastRefresh.value = timestamp;

            // Mettre à jour la map pour un accès rapide
            const map = new Map();
            parsedParams.forEach(param => {
                map.set(param.key, param.value);
            });
            parameterMap.value = map;

            return true;
        } catch (err) {
            console.error('Erreur lors du chargement des paramètres depuis le localStorage', err);
            return false;
        }
    }

    /**
     * Sauvegarde les paramètres dans le localStorage
     */
    function saveToLocalStorage() {
        try {
            localStorage.setItem(localStorageKey, JSON.stringify(parameters.value));
            localStorage.setItem(localStorageTimeKey, lastRefresh.value.toString());
        } catch (err) {
            console.error('Erreur lors de la sauvegarde des paramètres dans le localStorage', err);
        }
    }

    /**
     * Charge les paramètres depuis le serveur si nécessaire
     * @param {boolean} force Forcer le rechargement des paramètres
     * @returns {Promise<boolean>} true si les paramètres ont été chargés depuis le serveur
     */
    async function loadParameters(force = false) {
        // Ne pas charger si l'utilisateur n'est pas connecté
        if (!isConnected.value) return false;

        // Ne pas charger si déjà en cours de chargement
        if (loading.value) return false;

        const now = Date.now();
        const shouldRefresh = force || lastRefresh.value === 0 || (now - lastRefresh.value) > cacheTime;

        // Utiliser la version en cache si elle est récente
        if (!shouldRefresh) return false;

        loading.value = true;
        error.value = null;

        try {
            const endpoint = '/admin/parameters/';
            const response = await http.get(endpoint);

            parameters.value = response;
            lastRefresh.value = now;

            // Mettre à jour la map des paramètres pour un accès rapide
            const map = new Map();
            response.forEach(param => {
                map.set(param.key, param.value);
            });
            parameterMap.value = map;

            // Sauvegarder dans le localStorage
            saveToLocalStorage();

            return true;
        } catch (err) {
            console.error('Erreur lors du chargement des paramètres', err);
            error.value = err;
            return false;
        } finally {
            loading.value = false;
        }
    }

    /**
     * Récupère la valeur d'un paramètre
     * @param {string} key Clé du paramètre
     * @param {any} defaultValue Valeur par défaut si le paramètre n'existe pas
     * @returns {any} Valeur du paramètre ou valeur par défaut
     */
    function get(key, defaultValue = null) {
        if (!parameterMap.value.has(key)) {
            return defaultValue;
        }
        return parameterMap.value.get(key);
    }

    /**
     * Récupère la valeur numérique d'un paramètre
     * @param {string} key Clé du paramètre
     * @param {number} defaultValue Valeur par défaut si le paramètre n'existe pas ou n'est pas un nombre
     * @returns {number} Valeur numérique du paramètre ou valeur par défaut
     */
    function getNumber(key, defaultValue = 0) {
        const value = get(key);
        if (value === null) return defaultValue;

        const num = Number(value);
        return isNaN(num) ? defaultValue : num;
    }

    /**
     * Récupère la valeur booléenne d'un paramètre
     * @param {string} key Clé du paramètre
     * @param {boolean} defaultValue Valeur par défaut si le paramètre n'existe pas
     * @returns {boolean} Valeur booléenne du paramètre ou valeur par défaut
     */
    function getBoolean(key, defaultValue = false) {
        const value = get(key);
        if (value === null) return defaultValue;

        return value.toLowerCase() === 'true' || value === '1';
    }

    /**
     * Rafraîchit les paramètres depuis le serveur (ignore le cache)
     */
    async function refresh() {
        return loadParameters(true);
    }

    /**
     * Nettoie le cache des paramètres
     */
    function clearCache() {
        try {
            localStorage.removeItem(localStorageKey);
            localStorage.removeItem(localStorageTimeKey);
            parameters.value = [];
            parameterMap.value = new Map();
            lastRefresh.value = 0;
        } catch (err) {
            console.error('Erreur lors du nettoyage du cache des paramètres', err);
        }
    }

    loadFromLocalStorage();
    // Si on a des données en cache mais qu'elles sont trop anciennes,
    // on les utilise quand même en attendant le rafraîchissement
    loadParameters();

    // Si l'utilisateur se connecte, on recharge les paramètres
    watch(() => isConnected.value, (newValue) => {
        if (newValue) loadParameters();
    });

    return {
        // Données
        parameters: computed(() => parameters.value),
        loading: computed(() => loading.value),
        error: computed(() => error.value),
        lastRefresh: computed(() => lastRefresh.value),

        // Fonctions
        get,
        getNumber,
        getBoolean,
        loadParameters,
        refresh,
        clearCache
    };
}

export default useParameters;
